import React, { useState } from 'react';
import { MapPin, Phone, Mail, Send, User, Building, MessageSquare, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { companyInfo } from '../data/mock';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    interest: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_key: '45bc260a-21f7-40f6-8a18-f3c546036116',
          subject: `New Contact Form Submission - ${formData.interest || 'General Inquiry'}`,
          from_name: 'Sen & Co Website',
          name: formData.name,
          email: formData.email,
          company: formData.company,
          phone: formData.phone,
          interest: formData.interest,
          message: formData.message,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitted(true);
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          interest: '',
          message: ''
        });
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        setError('Failed to send message. Please try again.');
      }
    } catch (err) {
      setError('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const interestOptions = [
    'Become a Dealer',
    'Product Inquiry - Gulf',
    'Product Inquiry - Surya',
    'Product Inquiry - Selenide',
    'Partnership Opportunity',
    'General Inquiry'
  ];

  // Google Maps embed URL for the location
  const mapEmbedUrl = `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3683.8089!2d88.3639!3d22.5726!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjLCsDM0JzIxLjQiTiA4OMKwMjEnNTAuMCJF!5e0!3m2!1sen!2sin!4v1702000000000!5m2!1sen!2sin`;

  return (
    <div className="bg-white">
      {/* Hero Section with Banner */}
      <section 
        className="relative text-white py-56 lg:py-80 overflow-hidden bg-cover bg-bottom bg-no-repeat"
        style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_176ab01e-388f-4bea-9c6b-49caaa144cc8/artifacts/k9ciwkao_Contact_page_banner.png)' }}
      >
        {/* Light overlay for text readability */}
        <div className="absolute inset-0 bg-black/30" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-left max-w-xl">
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold mb-6 drop-shadow-lg">
              Contact Us
            </h1>
            <p className="text-xl text-white/90 drop-shadow">
              Ready to partner with us? Get in touch with our team today.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info - Left Side */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-[#1a237e] mb-6">Get In Touch</h2>
                <p className="text-gray-600 leading-relaxed">
                  Whether you&apos;re looking to become a dealer, explore our products, or have any questions, we&apos;re here to help.
                </p>
              </div>

              <Card className="border-0 shadow-lg bg-white">
                <CardContent className="p-6 space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#c41e3a]/10 rounded-full flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-[#c41e3a]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Address</h3>
                      <p className="text-gray-600 text-sm">
                        {companyInfo.contact.address}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#1a237e]/10 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-[#1a237e]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Contact Person</h3>
                      <p className="text-gray-600 text-sm">
                        {companyInfo.contact.contactName}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <Phone className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Phone</h3>
                      <div className="space-y-2">
                        <div>
                          <span className="text-gray-700 text-sm font-medium">Head Office (Girish Park):</span>
                          <a
                            href="tel:9051922724"
                            className="text-gray-600 text-sm hover:text-[#c41e3a] transition-colors ml-2"
                          >
                            90519 22724
                          </a>
                        </div>
                        <div className="text-gray-700 text-sm font-medium">Branches:</div>
                        <div className="pl-2 space-y-1">
                          <div>
                            <span className="text-gray-600 text-sm">Raja Bazar:</span>
                            <a
                              href="tel:9674130724"
                              className="text-gray-600 text-sm hover:text-[#c41e3a] transition-colors ml-2"
                            >
                              96741 30724
                            </a>
                          </div>
                          <div>
                            <span className="text-gray-600 text-sm">Barasat:</span>
                            <a
                              href="tel:9836277724"
                              className="text-gray-600 text-sm hover:text-[#c41e3a] transition-colors ml-2"
                            >
                              98362 77724
                            </a>
                          </div>
                          <div>
                            <span className="text-gray-600 text-sm">Baharampur:</span>
                            <a
                              href="tel:7044074724"
                              className="text-gray-600 text-sm hover:text-[#c41e3a] transition-colors ml-2"
                            >
                              70440 74724
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                      <Mail className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                      <a
                        href={`mailto:${companyInfo.contact.email}`}
                        className="text-gray-600 text-sm hover:text-[#c41e3a] transition-colors break-all"
                      >
                        {companyInfo.contact.email}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form - Right Side */}
            <div className="lg:col-span-3">
              <Card className="border-0 shadow-xl bg-white">
                <CardContent className="p-8 md:p-10">
                  {submitted ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-10 h-10 text-green-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">Thank You!</h3>
                      <p className="text-gray-600">
                        Your message has been sent successfully. Our team will get back to you shortly.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="mb-8">
                        <h2 className="text-2xl font-bold text-[#1a237e] mb-2">Send Us a Message</h2>
                        <p className="text-gray-600">Fill out the form below and we&apos;ll get back to you as soon as possible.</p>
                      </div>

                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="name" className="text-gray-700 flex items-center">
                              <User size={16} className="mr-2" />
                              Your Name *
                            </Label>
                            <Input
                              id="name"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              required
                              className="h-12"
                              placeholder="Enter your name"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="company" className="text-gray-700 flex items-center">
                              <Building size={16} className="mr-2" />
                              Company Name
                            </Label>
                            <Input
                              id="company"
                              name="company"
                              value={formData.company}
                              onChange={handleChange}
                              className="h-12"
                              placeholder="Enter company name"
                            />
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="email" className="text-gray-700 flex items-center">
                              <Mail size={16} className="mr-2" />
                              Email Address *
                            </Label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleChange}
                              required
                              className="h-12"
                              placeholder="Enter your email"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="phone" className="text-gray-700 flex items-center">
                              <Phone size={16} className="mr-2" />
                              Phone Number *
                            </Label>
                            <Input
                              id="phone"
                              name="phone"
                              type="tel"
                              value={formData.phone}
                              onChange={handleChange}
                              required
                              className="h-12"
                              placeholder="Enter phone number"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="interest" className="text-gray-700">I&apos;m Interested In *</Label>
                          <select
                            id="interest"
                            name="interest"
                            value={formData.interest}
                            onChange={handleChange}
                            required
                            className="w-full h-12 px-4 rounded-md border border-gray-200 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#c41e3a]"
                          >
                            <option value="">Select an option</option>
                            {interestOptions.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="message" className="text-gray-700 flex items-center">
                            <MessageSquare size={16} className="mr-2" />
                            Your Message *
                          </Label>
                          <Textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            required
                            rows={5}
                            placeholder="Tell us about your requirements..."
                            className="resize-none"
                          />
                        </div>

                        {error && (
                          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                            {error}
                          </div>
                        )}

                        <Button
                          type="submit"
                          size="lg"
                          disabled={isSubmitting}
                          className="w-full bg-[#c41e3a] hover:bg-[#a01830] text-white py-6 text-lg disabled:opacity-50"
                        >
                          {isSubmitting ? 'Sending...' : 'Send Message'}
                          <Send size={20} className="ml-2" />
                        </Button>
                      </form>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Google Map - Full Width Below */}
          <div className="mt-12">
            <Card className="border-0 shadow-lg overflow-hidden bg-white">
              <div className="aspect-[21/9] w-full">
                <iframe
                  src={mapEmbedUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Sen & Company Location"
                  className="w-full h-full min-h-[400px]"
                />
              </div>
              <div className="p-4 bg-gray-50 border-t">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <MapPin size={18} className="text-[#c41e3a]" />
                    <span className="text-sm">{companyInfo.contact.address}</span>
                  </div>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(companyInfo.contact.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#1a237e] text-sm font-medium hover:underline"
                  >
                    Open in Google Maps →
                  </a>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
